import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import Button from "./Button";

/**
 * App-wide confirmation overlay.
 *
 * - Default (`nested={false}`): portals to `document.body` with a high z-index for
 *   use next to the main app shell (e.g. delete sale on SalePage).
 * - Nested (`nested`): positions inside a parent modal shell (e.g. DetailModal
 *   `topLayer`) so it stacks above that modal’s content, same pattern as other
 *   in-modal overlays.
 */
const ConfirmDialog = ({
    open,
    title,
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    confirmVariant = "danger",
    onConfirm,
    onCancel,
    loading = false,
    nested = false,
    hideCancel = false,
}) => {
    const instanceIdRef = useRef(null);
    if (instanceIdRef.current == null) {
        instanceIdRef.current = `confirm-${Math.random().toString(36).slice(2, 11)}`;
    }
    const titleId = `${instanceIdRef.current}-title`;
    const descId = `${instanceIdRef.current}-desc`;

    useEffect(() => {
        if (!open || !onCancel) return;
        const onKey = (e) => {
            if (e.key === "Escape") onCancel();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onCancel]);

    if (!open) return null;

    const shellClass = nested
        ? "absolute inset-0 z-40 flex items-center justify-center px-4"
        : "fixed inset-0 z-[10000] flex items-center justify-center px-4";

    const dialog = (
        <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={description ? descId : undefined}
            className={`${shellClass} isolate`}
        >
            {/* z-0 keeps this behind the panel so it cannot intercept hover on buttons */}
            <button
                type="button"
                aria-label="Cancel"
                onClick={onCancel}
                className="absolute inset-0 z-0 bg-ink-900 bg-opacity-50"
            />
            <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-elevated">
                {title && (
                    <h3 id={titleId} className="text-lg font-semibold text-ink-900">
                        {title}
                    </h3>
                )}
                {description && (
                    <p id={descId} className="mt-2 text-sm text-ink-600">
                        {description}
                    </p>
                )}
                <div className="relative z-10 mt-6 flex justify-end gap-2">
                    {!hideCancel && (
                        <Button variant="secondary" onClick={onCancel} disabled={loading}>
                            {cancelLabel}
                        </Button>
                    )}
                    <Button variant={confirmVariant} onClick={onConfirm} disabled={loading}>
                        {loading ? "Working..." : confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );

    if (nested) {
        return dialog;
    }

    return createPortal(dialog, document.body);
};

ConfirmDialog.propTypes = {
    open: PropTypes.bool,
    title: PropTypes.node,
    description: PropTypes.node,
    confirmLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    confirmVariant: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    nested: PropTypes.bool,
    hideCancel: PropTypes.bool,
};

export default ConfirmDialog;
